import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Package, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Account = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/orders/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-5xl mx-auto"
      >
        <div className="mb-8 inline-block px-6 py-3 border-2 border-[#D4AF37] bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 shadow-[0_0_30px_rgba(212,175,55,0.3)]">
          <p className="text-xs text-white tracking-[0.3em] mb-1">DIAMOND MEMBER</p>
          <h1 className="text-4xl font-black text-[#D4AF37]" data-testid="account-title">MY ACCOUNT</h1>
        </div>
        <p className="text-xl text-white/70 mb-12" data-testid="account-welcome">Welcome back, {user.contact_person}!</p>

        <div className="mb-12 p-6 border-2 border-[#D4AF37] bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 shadow-[0_0_20px_rgba(212,175,55,0.3)]">
          <h2 className="text-2xl font-bold mb-4 text-[#D4AF37]">Account Details</h2>
          <div className="space-y-2">
            <p data-testid="account-company-name"><span className="text-white/50">Company Name:</span> {user.company_name}</p>
            <p data-testid="account-company-reg"><span className="text-white/50">Company Registration:</span> {user.company_registration}</p>
            <p data-testid="account-contact-person"><span className="text-white/50">Contact Person:</span> {user.contact_person}</p>
            <p data-testid="account-phone"><span className="text-white/50">Phone:</span> {user.contact_phone}</p>
            <p data-testid="account-email"><span className="text-white/50">Email:</span> {user.email}</p>
            <p data-testid="account-role"><span className="text-white/50">Role:</span> {user.role}</p>
          </div>
        </div>

        <h2 className="text-3xl font-bold mb-6 text-[#D4AF37]" data-testid="order-history-title">Order History</h2>

        {loading ? (
          <p className="text-white/50" data-testid="loading-orders">Loading orders...</p>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 border border-white/10" data-testid="no-orders">
            <Package size={48} className="mx-auto mb-4 text-white/20" />
            <p className="text-white/50">No orders yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, index) => (
              <div key={order.id} data-testid={`order-${index}`} className="border border-white/10 p-6 hover:border-white/20 transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm text-white/50 mb-1">Order ID</p>
                    <p className="font-mono text-sm" data-testid={`order-id-${index}`}>{order.id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-white/50 mb-1">Total</p>
                    <p className="text-xl font-bold text-[#FF4500]" data-testid={`order-total-${index}`}>R{order.total.toFixed(2)}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-white/50 mb-2">Items</p>
                  {order.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex justify-between text-sm" data-testid={`order-item-${index}-${itemIndex}`}>
                      <span>{item.product_name} x{item.quantity}</span>
                      <span>R{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center text-sm text-white/50">
                  <div className="flex items-center gap-2">
                    <Calendar size={16} />
                    <span data-testid={`order-date-${index}`}>{new Date(order.created_at).toLocaleDateString()}</span>
                  </div>
                  <span className="px-3 py-1 border border-white/10 text-xs uppercase" data-testid={`order-status-${index}`}>{order.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Account;